import type { ColumnDef } from "@tanstack/react-table";
import type { IMaterial } from "../../../modelos/Materiales/entidades/IMaterial";
import { ActionIcon, Badge, Group, Text, Tooltip, TextInput, Switch, Select } from "@mantine/core";
import { IconEdit, IconTrash, IconCheck, IconX } from "@tabler/icons-react";
import type { ITipoMaterial } from "../../../modelos/Materiales/entidades/ITipoMaterial";
import React from "react";

interface IConstantesTablaMaterialProps {
    manejarEdicionMaterial: (material: IMaterial) => void;
    manejarEliminacionMaterial: (material: IMaterial) => void;
    modoEdicionLinea?: boolean;
    filaEnEdicion?: number | null; // ✅ Cambiar a number
    onCambioValor?: (materialId: number, campo: keyof IMaterial, valor: IMaterial[keyof IMaterial]) => void; // ✅ Cambiar signature
    onGuardarEdicion?: (materialId: number) => void; // ✅ Cambiar signature
    onCancelarEdicion?: () => void;
    tiposMateriales: ITipoMaterial[];
}

// const simbolosMoneda: Record<string, string> = {
//     COP: "$",
//     CRC: "₡",
//     PAB: "B/.",
//     USD: "US$",
// };


// ✅ Componente memoizado para Switch de Activo
const CampoActivoEditable = React.memo(({
    activo,
    materialId,
    onCambioValor
}: {
    activo: boolean;
    materialId: number;
    onCambioValor: (materialId: number, campo: keyof IMaterial, valor: IMaterial[keyof IMaterial]) => void;
}) => {
    return (
        <Switch
            checked={activo}
            onChange={(e) => onCambioValor(materialId, "Activo", e.currentTarget.checked)}
            size="xs"
        />
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.activo === nextProps.activo &&
        prevProps.materialId === nextProps.materialId
    );
});

export const columnasTablaMaterial = ({
    manejarEdicionMaterial,
    manejarEliminacionMaterial,
    modoEdicionLinea = false,
    filaEnEdicion = null,
    onCambioValor,
    onGuardarEdicion,
    onCancelarEdicion,
    tiposMateriales,
}: IConstantesTablaMaterialProps): ColumnDef<IMaterial>[] => [
        {
            id: "actions",
            header: "Acciones",
            cell: ({ row }) => {
                // ✅ Usar row.original.Id en lugar de row.id
                const materialId = row.original.Id;
                const estaEnEdicion = modoEdicionLinea && filaEnEdicion === materialId;

                if (estaEnEdicion && onGuardarEdicion && onCancelarEdicion) {
                    return (
                        <Group gap={4} justify="center">
                            <Tooltip
                                label="Guardar"
                                withinPortal
                                openDelay={100}
                                zIndex={9999}
                                style={{ fontSize: '10px', padding: '3px 5px' }}
                            >
                                <ActionIcon
                                    variant="subtle"
                                    color="var(--mantine-color-primary-0)"
                                    size="sm"
                                    radius="xl"
                                    onClick={() => onGuardarEdicion(materialId)}
                                >
                                    <IconCheck size={18} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip
                                label="Cancelar"
                                withinPortal
                                openDelay={100}
                                zIndex={9999}
                                style={{ fontSize: '10px', padding: '3px 5px' }}
                            >
                                <ActionIcon
                                    variant="subtle"
                                    color="#df675a"
                                    size="sm"
                                    radius="xl"
                                    onClick={onCancelarEdicion}
                                >
                                    <IconX size={18} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    );
                }

                // ✅ Determinar si hay otra fila en edición
                const hayOtraFilaEnEdicion = filaEnEdicion !== null && filaEnEdicion !== materialId;

                return (
                    <Group gap={4} justify="center">
                        <Tooltip
                            label={hayOtraFilaEnEdicion ? "Termina la edición actual" : "Editar"}
                            withinPortal
                            openDelay={100}
                            zIndex={9999}
                            style={{ fontSize: '10px', padding: '3px 5px' }}
                        >
                            <ActionIcon
                                color='var(--mantine-color-primary-0)'
                                variant="transparent"
                                radius="sm"
                                size="sm"
                                onClick={() => manejarEdicionMaterial(row.original)}
                                disabled={hayOtraFilaEnEdicion}
                            >
                                <IconEdit size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip
                            label={hayOtraFilaEnEdicion ? "Termina la edición actual" : "Eliminar"}
                            withinPortal
                            openDelay={100}
                            zIndex={9999}
                            style={{ fontSize: '10px', padding: '3px 5px' }}
                        >
                            <ActionIcon
                                color="var(--mantine-color-primary-0)"
                                variant="transparent"
                                radius="sm"
                                size="sm"
                                onClick={() => manejarEliminacionMaterial(row.original)}
                                disabled={hayOtraFilaEnEdicion}
                            >
                                <IconTrash size={18} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                );
            },
            size: 120,
        },

        {
            accessorKey: "Sku",
            header: "Código",
            cell: ({ getValue, row }) => {
                const materialId = row.original.Id;
                const estaEnEdicion = modoEdicionLinea && filaEnEdicion === materialId;
                const valor = getValue() as string;

                if (estaEnEdicion && onCambioValor) {

                    return (
                        <TextInput
                            value={valor || ""}
                            maxLength={15}
                            onChange={(e) => {
                                const valor = e.currentTarget.value;
                                const regex = /^[A-Za-z0-9-_]*$/;
                                if (regex.test(valor)) {
                                    onCambioValor(materialId, "Sku", valor);
                                }
                            }}
                            size="xs"
                            placeholder="Código*"
                            styles={{
                                input: {
                                    fontSize: "12px",
                                    padding: "4px 8px",
                                }
                            }}
                        />
                    );
                }

                return <Text size="xs">{valor}</Text>;
            },
        },

        {
            accessorKey: "Nombre",
            header: "Nombre",
            cell: ({ getValue, row }) => {
                const materialId = row.original.Id;
                const estaEnEdicion = modoEdicionLinea && filaEnEdicion === materialId;
                const valor = getValue() as string;

                if (estaEnEdicion && onCambioValor) {

                    return (
                        <TextInput
                            value={valor || ""}
                            onChange={(e) => {
                                onCambioValor(materialId, "Nombre", e.currentTarget.value);
                            }}
                            size="xs"
                            maxLength={30}
                            placeholder="Nombre*"
                            styles={{
                                input: {
                                    fontSize: "12px",
                                    padding: "4px 8px",
                                }
                            }}
                        />
                    );
                }

                return <Text size="xs">{valor}</Text>;
            },
        },

        {
            accessorKey: "TipoMaterial",
            header: "Tipo",
            cell: ({ row }) => {
                const materialId = row.original.Id;
                const estaEnEdicion = modoEdicionLinea && filaEnEdicion === materialId;
                const valor = row.original.TipoMaterialId;
                const descripcionTipo = tiposMateriales.find(tp => tp.Id === row.original.TipoMaterialId);

                if (estaEnEdicion && onCambioValor) {

                    return (
                        <Select
                            placeholder="Tipo Material*"
                            data={tiposMateriales.map(tp => ({
                                value: tp.Id.toString(),
                                label: tp.Nombre
                            }))}
                            size='xs'
                            value={valor ? valor.toString() : null}
                            onChange={(value) => {
                                onCambioValor(materialId, 'TipoMaterialId', value ? parseInt(value) : 0)
                            }}
                            style={{ minWidth: 200 }}
                        />
                    );
                }

                return <Text size="xs">{descripcionTipo ? descripcionTipo.Nombre : ''}</Text>;
            },
            size: 120,
        },

        // {
        //     accessorKey: "PrecioLista",
        //     header: "Precio",
        //     cell: ({ getValue, row }) => {
        //         const materialId = row.original.Id;
        //         const estaEnEdicion = modoEdicionLinea && filaEnEdicion === materialId;
        //         const valor = getValue() as number;

        //         if (estaEnEdicion && onCambioValor) {

        //             return (
        //                 <NumberInput
        //                     min={0}
        //                     value={valor || 0}
        //                     onChange={(nuevoValor) => {
        //                         onCambioValor(materialId, "PrecioLista", nuevoValor)
        //                     }}
        //                     size="xs"
        //                     prefix={`${simbolosMoneda[row.original.Moneda || "COP"]} `}
        //                     thousandSeparator="."
        //                     decimalSeparator=","
        //                     maxLength={18}
        //                     placeholder="Precio*"
        //                     hideControls={false}
        //                     leftSectionWidth={78}
        //                     leftSection={
        //                         <Select
        //                             data={[
        //                                 { value: "COP", label: "🇨🇴 COP" },
        //                                 { value: "CRC", label: "🇨🇷 CRC" },
        //                                 { value: "PAB", label: "🇵🇦 PAB" },
        //                                 { value: "USD", label: "🇺🇸 USD" },
        //                             ]}
        //                             value={row.original.Moneda || "COP"}
        //                             onChange={(v) => {
        //                                 onCambioValor(materialId, "Moneda", v ? v : 'COP')
        //                             }}
        //                             size="xs"
        //                             styles={{
        //                                 input: {
        //                                     width: "100%",
        //                                     height: "100%",
        //                                     border: "none",
        //                                     background: "transparent",
        //                                     fontSize: "12px",
        //                                     padding: "0 2px",
        //                                     textAlign: "center",
        //                                 },
        //                                 dropdown: { zIndex: 9999 },
        //                             }}
        //                         />
        //                     }
        //                     styles={{
        //                         root: {
        //                             width: "100%",
        //                             display: "flex",
        //                             alignItems: "center",
        //                             flexDirection: 'column'
        //                         },
        //                         section: {
        //                             display: "flex",
        //                             alignItems: "center",
        //                             justifyContent: "center",
        //                             padding: 0,
        //                         },
        //                     }}
        //                 />
        //             );
        //         }

        //         return (
        //             <Text size="xs">
        //                 {`${simbolosMoneda[row.original.Moneda || "COP"]} ${new Intl.NumberFormat("es-CO").format(valor ?? 0)}`}
        //             </Text>
        //         );
        //     },
        //     size: 150,
        // },

        {
            accessorKey: "Activo",
            header: "Estado",
            cell: ({ getValue, row }) => {
                const materialId = row.original.Id;
                const estaEnEdicion = modoEdicionLinea && filaEnEdicion === materialId;
                const activo = getValue() as boolean;

                if (estaEnEdicion && onCambioValor) {
                    return (
                        <CampoActivoEditable
                            activo={activo}
                            materialId={materialId}
                            onCambioValor={onCambioValor}
                        />
                    );
                }

                return (
                    <Badge
                        color={activo ? "var(--mantine-color-primary-0)" : "#df675a"}
                        variant="light"
                        size="xs"
                    >
                        {activo ? "Activo" : "Inactivo"}
                    </Badge>
                );
            },
            size: 100,
        },
    ];